import AiIcon from "../../Core/AiIcon/AiIcon";
import React, { useCallback } from "react";
import { Tooltip } from "@chakra-ui/react";

import { cn } from "@/lib/utils";
import { useAiAssistantStore } from "../../../hooks/aiAssistant.hooks";
import { useBetaFeatureEnabled } from "../../../hooks/betaFeatures";
import useKeyDown from "../../../hooks/useKeyDown";

/**
 * Whether the current user has switched the assistant on for themselves, under
 * Inställningar → Avancerat → Beta-funktionalitet. That beta flag is the only
 * gate: the backend no longer requires an organisation flag or a dedicated
 * scope, it only refuses callers who are not dashboard users. Write tools still
 * go through their own approval step, and every proxied tool call is made as
 * the caller, so it can reach exactly what the caller could reach anyway.
 */
export function useAiAssistantEnabled() {
	return useBetaFeatureEnabled("aiAssistant");
}

/** Shortcut label matches the modifier the handler listens for on this platform. */
const isMac = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform);

interface AiAssistantTriggerProps {
	className?: string;
}

/**
 * Side-menu button (and ⌘J / Ctrl+J shortcut) that toggles the assistant
 * panel, sized to sit beside the global-search button. Renders nothing when
 * the assistant is not available to the user.
 */
const AiAssistantTrigger: React.FC<AiAssistantTriggerProps> = ({ className }) => {
	const enabled = useAiAssistantEnabled();
	const { isOpen, toggle } = useAiAssistantStore();

	const onKeyDown = useCallback(
		(event: KeyboardEvent) => {
			if (!enabled) return;
			if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "j") {
				event.preventDefault();
				toggle();
			}
		},
		[enabled, toggle]
	);

	useKeyDown({ onKeyDown });

	if (!enabled) return null;

	return (
		<Tooltip label={`AI-assistent (${isMac ? "⌘J" : "Ctrl+J"})`} placement="right">
			<button
				type="button"
				onClick={toggle}
				aria-pressed={isOpen}
				className={cn(
					"flex h-9 w-9 cursor-pointer items-center justify-center rounded-[10px] text-white",
					isOpen ? "bg-brand opacity-100" : "bg-[#555] opacity-60 hover:opacity-80",
					className
				)}
			>
				<AiIcon size={20} strokeWidth={1.1} />
			</button>
		</Tooltip>
	);
};

export default AiAssistantTrigger;
